/**
 * sanitizeTcHtml — IDI-624 (D1–D3, D5). The ONE place stored T&C HTML is
 * cleaned before any page renders it. Every HTML write into the DOM under
 * src/ must go through this function (guarded by htmlWriteGuard.idi624.test.js).
 *
 * D2 allowlist: p br div span b strong i em u sup sub h1–h6 ul ol li
 *   table thead tbody tr th td a. Other tags are removed but their text is
 *   kept; script/style (and DOMPurify's other FORBID_CONTENTS tags) are
 *   removed together with their contents. The only attribute kept is a
 *   link's href; href on any other tag is removed (IDI-641 D5).
 * D3 links: http, https, mailto, tel stay (same tab — no target attr);
 *   any other href is dropped and the anchor is unwrapped to plain text.
 * D5 a result with no visible text returns '' so callers can hide the
 *   "Terms & conditions" section entirely.
 */
import createDOMPurify from 'dompurify';

const TC_ALLOWED_TAGS = Object.freeze([
  'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 'sup', 'sub',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a',
]);

const TC_ALLOWED_HREF = /^(?:https?:|mailto:|tel:)/i;

let purifier = null;
function getPurifier() {
  if (purifier) return purifier;
  if (typeof window === 'undefined') return null;
  // Local instance: hooks/config never leak into any other DOMPurify user.
  purifier = createDOMPurify(window);
  return purifier.isSupported ? purifier : null;
}

const CONFIG = {
  ALLOWED_TAGS: [...TC_ALLOWED_TAGS],
  ALLOWED_ATTR: ['href'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  KEEP_CONTENT: true,
  ALLOWED_URI_REGEXP: TC_ALLOWED_HREF,
  // String mode (DOMPurify's default, stable across majors). The D3 pass
  // below re-parses it with DOMParser into an inert document instead of
  // relying on RETURN_DOM's node type, which has varied between versions.
};

// Characters a browser may ignore inside a URL scheme (controls, spaces,
// zero-width chars, BOM, soft hyphen, …): anything outside printable ASCII.
const NON_PRINTABLE_ASCII = /[^\x21-\x7E]/g;

function unwrap(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

/**
 * @param {string} html stored T&C HTML (untrusted)
 * @returns {string} cleaned HTML, or '' when nothing visible survives (D5)
 */
export function sanitizeTcHtml(html) {
  if (typeof html !== 'string' || !html) return '';
  const dp = getPurifier();
  if (!dp) return '';
  const cleaned = dp.sanitize(html, CONFIG);
  if (typeof cleaned !== 'string' || !cleaned) return '';
  // DOMParser documents are inert: nothing loads or runs while we walk it.
  const { body } = new window.DOMParser().parseFromString(cleaned, 'text/html');
  // IDI-641 D5: href is a link attribute only; other tags keep their text.
  body.querySelectorAll('[href]:not(a)').forEach((el) => el.removeAttribute('href'));
  // D3: an <a> without a surviving (allowed-scheme) href becomes plain text.
  Array.from(body.querySelectorAll('a')).forEach((a) => {
    const href = (a.getAttribute('href') || '').replace(NON_PRINTABLE_ASCII, '');
    if (!href || !TC_ALLOWED_HREF.test(href)) unwrap(a);
  });
  // D5: whitespace-only / text-empty result → no Terms section.
  if (!(body.textContent || '').trim()) return '';
  return body.innerHTML;
}

/** D5 helper: true when the cleaned HTML has no visible text. */
export function isEmptyTcHtml(html) {
  return sanitizeTcHtml(html) === '';
}

export default sanitizeTcHtml;
